import AddressBlock from '@app/components/AddressBlock'
import AssignmentForm from '@app/components/AssignmentForm'
import AssociatedContactsSelector from '@app/components/AssociatedContactsSelector'
import CheckBox from '@app/components/CheckBox'
import CompanySelector from '@app/components/CompanySelector'
import { DataList, DataListItem } from '@app/components/DataList'
import DateSelector from '@app/components/DateSelector'
import EditPageContainer from '@app/components/EditPageContainer'
import FormGroupRenderer from '@app/components/FormGroupRenderer'
import LeasePriceBreakdownEditor from '@app/components/LeasePriceBreakdownEditor'
import LeaseTermDetailsCard from '@app/components/LeaseTermDetailsCard'
import PageSection from '@app/components/PageSection'
import PipelineCommissionFeeEditor from '@app/components/PipelineCommissionFeeEditor'
import PipelineStatusSelector from '@app/components/PipelineStatusSelector'
import { Assignment } from '@app/models/Assignment'
import { BrokeragePipeline } from '@app/models/BrokeragePipeline'
import { ListingType, PipelineStatus } from '@app/models/enums'
import { bindFunctions } from '@app/utils/class'
import {
  formatCurrency,
  formatDate,
  formatListingSize,
  humanize,
} from '@app/utils/formatting'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import * as qs from 'query-string'
import * as React from 'react'
import DocumentTitle from 'react-document-title'
import { RouteComponentProps } from 'react-router-dom'
import {
  Alert,
  Button,
  Container,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Table,
} from 'reactstrap'
import { actionCreators, IPipelineEditState } from '../modules/pipelineEdit'
import './PipelineEditPage.scss'

type PipelineEditProps = IPipelineEditState &
  typeof actionCreators &
  RouteComponentProps<{ id?: string }>

export default class PipelineEditPage extends React.PureComponent<PipelineEditProps> {
  private assignmentFormRef: React.RefObject<HTMLFormElement>

  constructor(props: PipelineEditProps) {
    super(props)
    this.assignmentFormRef = React.createRef()
    bindFunctions(this)
  }

  public componentDidMount() {
    if (this.props.match.params.id) {
      this.props.loadEditPipeline(this.props.match.params.id)
    } else {
      const query = qs.parse(this.props.location.search)
      if (query.listingId) {
        this.props.loadAddPipeline(query.listingId)
      }
      this.props.checkPipelineExists(query.listingId)
    }
  }

  public componentWillUnmount() {
    this.props.resetState()
  }

  public render() {
    if (!this.props.isReady) {
      return (
        <DocumentTitle
          title={this.props.match.params.id ? 'Edit Pipeline' : 'New Pipeline'}
        >
          <Container className='my-3'>
            <div className='mb-3 text-center'>
              Retrieving Pipeline Information
            </div>
            {this.props.error && (
              <Alert color='danger'>{this.props.error}</Alert>
            )}
          </Container>
        </DocumentTitle>
      )
    }

    const isLease = this.props.pipeline.listing.type === ListingType.Lease

    return (
      <EditPageContainer
        saveDisabled={false}
        title={
          this.props.match.params.id
            ? `${this.props.pipeline.name} :: Edit`
            : 'New Listing'
        }
        onSaveClick={this._onSave}
        onCancelClick={this._onCancel}
      >
        <Container className='my-3'>
          {this._renderPipelineExists()}
          {this._renderListing()}
          {this._renderGeneralInformation(isLease)}
          {this._renderEarnestMoney(isLease)}
          {this._renderFeeBonusDistribution(isLease)}
          {this._renderBuyerInformation(isLease)}
          {this._renderSellerInformation(isLease)}
          {this._renderLeasePriceBreakdown(isLease)}
          {this._renderCriticalDates()}
        </Container>
      </EditPageContainer>
    )
  }

  private _onCancel() {
    this.props.history.goBack()
  }

  private _onSave() {
    this.props.savePipeline(this.props.pipeline as BrokeragePipeline)
  }

  private _renderPipelineExists() {
    return this.props.hasExistingPipeline ? (
      <div className='text-red d-flex justify-content-center bg-gray h6 py-2 pl-2'>
        A Pipeline With This Property Already Exists
      </div>
    ) : (
      ''
    )
  }

  private _renderBuyerInformation(isLease: boolean) {
    return (
      <PageSection title={isLease ? 'Tenant Information' : 'Buyer Information'}>
        <FormGroupRenderer labelText={isLease ? 'Tenant(s)' : 'Buyer(s)'}>
          <AssociatedContactsSelector
            name={isLease ? 'Tenants' : 'Buyers'}
            onChange={this.props.setBuyers}
            required
            value={this.props.pipeline.buyers}
          />
        </FormGroupRenderer>
        <FormGroupRenderer
          labelText={isLease ? "Tenant's Agent(s)" : 'Selling Agent(s)'}
        >
          <div className='mb-1'>
            <CheckBox
              onChange={this.props.setNoSellingAgent}
              value={this.props.pipeline.noSellingAgent}
            >
              {isLease ? "No Tenant's Agent" : 'No Selling Agent'}
            </CheckBox>
          </div>
          {!this.props.pipeline.noSellingAgent && (
            <AssociatedContactsSelector
              name={isLease ? 'TenantsAgent' : 'SellingAgent'}
              onChange={this.props.setSellingAgents}
              required
              value={this.props.pipeline.sellingAgents}
            />
          )}
        </FormGroupRenderer>
        {!isLease && (
          <FormGroupRenderer
            labelText={"Buyer's Lender"}
            name='BuyersLender'
            onChange={this.props.setBuyersLender}
            type='textarea'
            value={this.props.pipeline.buyerLender}
          />
        )}
        {!isLease && (
          <FormGroupRenderer labelText={'Title Company'}>
            <CompanySelector
              name='BuyersTitleCompany'
              onChange={this.props.setBuyersTitleCompany}
              value={this.props.pipeline.buyersTitleCompany}
            />
          </FormGroupRenderer>
        )}
      </PageSection>
    )
  }

  private _renderCriticalDates() {
    return (
      <PageSection title='Critical Dates'>
        <Button
          className='mb-2'
          color='primary'
          onClick={this.props.beginAddCriticalDate}
          outline
          size='sm'
        >
          Add critical date
        </Button>
        <Table className='edit-critical-dates-table' hover size='sm' striped>
          <thead>
            <tr>
              <th className='complete-col' />
              <th className='due-date-col'>Due Date</th>
              <th className='title-col'>Title</th>
              <th className='actions-col'> Actions</th>
            </tr>
          </thead>
          <tbody>
            {this.props.pipeline.criticalDates
              .sortBy((cd) => (cd === undefined ? undefined : cd.dueDate))
              .toArray()
              .map((critDate, idx) => (
                <tr key={critDate.id}>
                  <td className='complete-col'>
                    <CheckBox
                      value={critDate.isComplete}
                      onChange={(isComplete) =>
                        this.props.setCriticalDateComplete(
                          critDate.id,
                          isComplete,
                        )
                      }
                    />
                  </td>
                  <td className='due-date-col'>
                    {formatDate(critDate.dueDate, 'MM/DD/YYYY')}
                  </td>
                  <td className='title-col'>{critDate.title}</td>
                  <td className='actions-col'>
                    <Button
                      className='text-primary'
                      color='link'
                      onClick={() =>
                        this.props.beginEditCriticalDate(critDate as Assignment)
                      }
                      size='sm'
                      title='Edit'
                    >
                      <FontAwesomeIcon icon={['fal', 'pencil']} fixedWidth />
                    </Button>
                    <Button
                      className='text-danger'
                      color='link'
                      onClick={() => this.props.deleteCriticalDate(critDate.id)}
                      size='sm'
                      title='Delete'
                    >
                      <FontAwesomeIcon icon={['fal', 'trash']} fixedWidth />
                    </Button>
                  </td>
                </tr>
              ))}
          </tbody>
        </Table>
        <Modal
          isOpen={this.props.showCriticalDateModal}
          toggle={() => this.props.cancelEditCriticalDate()}
          size='lg'
        >
          <ModalHeader toggle={() => this.props.cancelEditCriticalDate()}>
            {this.props.editCriticalDateIndex === -1
              ? 'New Critical Date'
              : this.props.pipeline.criticalDates.get(
                  this.props.editCriticalDateIndex,
                ).title}
          </ModalHeader>
          <ModalBody style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            <form ref={this.assignmentFormRef}>
              <AssignmentForm />
            </form>
          </ModalBody>
          <ModalFooter>
            <Button
              color='secondary'
              onClick={() => this.props.cancelEditCriticalDate()}
              outline
            >
              Cancel
            </Button>
            <Button
              color='primary'
              onClick={() => {
                if (
                  this.assignmentFormRef.current &&
                  this.assignmentFormRef.current.checkValidity()
                ) {
                  this.props.endEditCriticalDate()
                }
              }}
            >
              OK
            </Button>
          </ModalFooter>
        </Modal>
      </PageSection>
    )
  }

  private _renderEarnestMoney(isLease: boolean) {
    if (isLease) {
      return null
    }

    return (
      <PageSection title='Earnest Money'>
        <FormGroupRenderer
          labelText='Amount'
          min={0}
          name='EarnestMoneyAmount'
          onChange={this.props.setEarnestMoney}
          required
          step='0.01'
          type='number'
          value={this.props.pipeline.earnestMoney}
        />
        <FormGroupRenderer labelText='Deposit Date'>
          <DateSelector
            name='EarnestMoneyDepositDate'
            onChange={this.props.setEarnestMoneyDepositDate}
            required={this.props.pipeline.earnestMoney > 0}
            value={this.props.pipeline.earnestMoneyDepositDate}
          />
        </FormGroupRenderer>
        <FormGroupRenderer
          labelText='Holding Location'
          name='EarnestMoneyHoldingLocation'
          onChange={this.props.setEarnestMoneyLocation}
          type='textarea'
          value={this.props.pipeline.earnestMoneyLocation}
        />
        <FormGroupRenderer labelText='Withdrawl Date'>
          <DateSelector
            minDate={this.props.pipeline.earnestMoneyDepositDate}
            name='EarnestMoneyWithdrawlDate'
            onChange={this.props.setEarnestMoneyWithdrawlDate}
            required={
              this.props.pipeline.earnestMoney > 0 &&
              (this.props.pipeline.status === PipelineStatus.Cancelled ||
                this.props.pipeline.status === PipelineStatus.Closed)
            }
            value={this.props.pipeline.earnestMoneyWithdrawlDate}
          />
        </FormGroupRenderer>
      </PageSection>
    )
  }

  private _renderFeeBonusDistribution(isLease: boolean) {
    return (
      <PageSection title='Fees / Bonus Distribution'>
        <div className='edit-fee-distribution'>
          <PipelineCommissionFeeEditor
            className='edit-fee-distribution-group left'
            title='Listing'
            onChange={this.props.setListingFee}
            value={this.props.pipeline.listingFee}
          />
          <PipelineCommissionFeeEditor
            className='edit-fee-distribution-group right'
            title={isLease ? 'Leasing' : 'Selling'}
            onChange={this.props.setSellingFee}
            value={this.props.pipeline.sellingFee}
          />
        </div>
      </PageSection>
    )
  }

  private _renderGeneralInformation(isLease: boolean) {
    return (
      <PageSection title='General Information'>
        <FormGroupRenderer
          labelText='Name'
          name='Name'
          onChange={this.props.setName}
          required
          value={this.props.pipeline.name}
        />
        <FormGroupRenderer labelText='Effective Date'>
          <DateSelector
            name='EffectiveDate'
            onChange={this.props.setEffectiveDate}
            required
            value={this.props.pipeline.effectiveDateOfOffer}
          />
        </FormGroupRenderer>
        <FormGroupRenderer
          labelText={isLease ? 'Execution Date' : 'Closing Date'}
        >
          <DateSelector
            name='EffectiveDate'
            onChange={this.props.setClosingDate}
            required
            value={this.props.pipeline.closingDate}
          />
        </FormGroupRenderer>
        <FormGroupRenderer
          labelText='Sales Price'
          name='SalesPrice'
          onChange={this.props.setSalesPrice}
          required
          step='0.01'
          type='number'
          value={this.props.pipeline.salesPrice}
        />
        <FormGroupRenderer labelText='Status'>
          <PipelineStatusSelector
            name='Status'
            onChange={this.props.setStatus}
            required
            value={this.props.pipeline.status}
          />
        </FormGroupRenderer>
        {!isLease && (
          <FormGroupRenderer>
            <CheckBox
              onChange={this.props.setIs1031Exchange}
              value={this.props.pipeline.is1031Exchange}
            >
              Is this a 1031 Exchange?
            </CheckBox>
          </FormGroupRenderer>
        )}
      </PageSection>
    )
  }

  private _renderLeasePriceBreakdown(isLease: boolean) {
    if (!isLease) {
      return null
    }

    const summary = this.props.pipeline.leasePriceBreakdown.toArray().reduce(
      (result, term) => {
        result.totalPrice += term.totalPerTerm
        result.totalFees += (term.totalPerTerm * term.fees) / 100
        return result
      },
      { totalPrice: 0, totalFees: 0 },
    )

    return (
      <PageSection title='Lease Price Breakdown'>
        <LeasePriceBreakdownEditor
          className='mb-2'
          color='primary'
          onSave={(newTerm) => this.props.setLeasePriceTerm(newTerm)}
          outline
          size='sm'
        >
          Add Term
        </LeasePriceBreakdownEditor>
        <div className='detail-card-group'>
          {this.props.pipeline.leasePriceBreakdown
            .toArray()
            .map((term, index) => (
              <div className='detail-card mb-2' key={`term-${index}`}>
                <LeaseTermDetailsCard
                  actions={[
                    <LeasePriceBreakdownEditor
                      className='mr-2'
                      color='secondary'
                      key='edit-button'
                      onSave={(newTerm) =>
                        this.props.setLeasePriceTerm(newTerm, index)
                      }
                      outline
                      size='sm'
                      term={term}
                    >
                      <FontAwesomeIcon icon={['fal', 'pencil']} fixedWidth />
                    </LeasePriceBreakdownEditor>,
                    <Button
                      color='danger'
                      key='delete-button'
                      onClick={() => this.props.deleteLeasePriceTerm(index)}
                      outline
                      size='sm'
                    >
                      <FontAwesomeIcon icon={['fal', 'trash']} fixedWidth />
                    </Button>,
                  ]}
                  term={term}
                />
              </div>
            ))}
        </div>
        <h6 className='section-title'>Totals</h6>
        <DataList>
          <DataListItem
            label='Price'
            value={formatCurrency(summary.totalPrice, 0)}
          />
          <DataListItem
            label='Fees'
            value={formatCurrency(summary.totalFees, 0)}
          />
        </DataList>
      </PageSection>
    )
  }

  private _renderListing() {
    return (
      <PageSection title='Listing'>
        <FormGroupRenderer labelText='Property'>
          <div>{this.props.pipeline.listing.property.name}</div>
          <AddressBlock
            address={this.props.pipeline.listing.property.address}
          />
        </FormGroupRenderer>
        {this.props.pipeline.listing.space && (
          <FormGroupRenderer labelText='Space'>
            {this.props.pipeline.listing.space.spaceNo}
          </FormGroupRenderer>
        )}
        {this.props.pipeline.listing.lot && (
          <FormGroupRenderer labelText='Lot'>
            {this.props.pipeline.listing.lot.lotNo}
          </FormGroupRenderer>
        )}
        <FormGroupRenderer labelText='Listing'>
          {this.props.pipeline.listing.title} (
          {humanize(this.props.pipeline.listing.type)})
        </FormGroupRenderer>
        <FormGroupRenderer labelText='Size'>
          {formatListingSize(this.props.pipeline.listing)}
        </FormGroupRenderer>
      </PageSection>
    )
  }

  private _renderSellerInformation(isLease: boolean) {
    return (
      <PageSection
        title={isLease ? 'Landlord Information' : 'Seller Information'}
      >
        <FormGroupRenderer labelText={isLease ? 'Landlord(s)' : 'Seller(s)'}>
          <AssociatedContactsSelector
            name={isLease ? 'Landlords' : 'Sellers'}
            onChange={this.props.setSellers}
            required
            value={this.props.pipeline.sellers}
          />
        </FormGroupRenderer>
        <FormGroupRenderer
          labelText={isLease ? "Landlord's Agent(s)" : 'Listing Agent(s)'}
        >
          <div className='mb-1'>
            <CheckBox
              onChange={this.props.setNoListingAgent}
              value={this.props.pipeline.noListingAgent}
            >
              {isLease ? "No Landlord's Agent" : 'No Listing Agent'}
            </CheckBox>
          </div>
          {!this.props.pipeline.noListingAgent && (
            <AssociatedContactsSelector
              name={isLease ? 'LandlordAgent' : 'ListingAgent'}
              onChange={this.props.setListingAgents}
              required
              value={this.props.pipeline.listingAgents}
            />
          )}
        </FormGroupRenderer>
        {!isLease && (
          <FormGroupRenderer
            labelText={"Seller's Mortgage"}
            name='SellersMortgage'
            onChange={this.props.setSellersMortgage}
            type='textarea'
            value={this.props.pipeline.sellerMortgage}
          />
        )}
        {!isLease && (
          <FormGroupRenderer labelText={'Title Company'}>
            <CompanySelector
              name='SellersTitleCompany'
              onChange={this.props.setSellersTitleCompany}
              value={this.props.pipeline.sellersTitleCompany}
            />
          </FormGroupRenderer>
        )}
      </PageSection>
    )
  }
}
